import React, { useMemo } from 'react'
import styled from '@emotion/styled'
import { GatsbyImage, getImage } from 'gatsby-plugin-image'
import { mediaQueries } from '../../theme'
import { H5 } from '../shared/type'
import BaseLink from '../shared/link'
import useLatestPost from './hooks/use-latest-post'

const MS_PER_DAY = 1000 * 60 * 60 * 24

const dateDiff = (a, b) => Math.floor(Math.abs(a - b) / MS_PER_DAY)

const useLabel = (post, maxAge = 30) => {
  const {
    frontmatter: { date, resource },
    fields: { slug },
  } = post
  const latestPost = useLatestPost(resource)
  return useMemo(() => {
    const isLatest = slug === latestPost?.fields?.slug
    const isNew = dateDiff(new Date(date), Date.now()) < maxAge

    if (isLatest) return 'Latest'
    if (isNew) return 'New'
    return ''
  }, [date, slug, latestPost, maxAge])
}

const Title = styled(H5)`
  grid-area: title;
  align-self: flex-start;
  margin: 0;
  line-height: 1.5;
  font-size: ${p => p.theme.fontSizes[2]};
`

const Author = styled.span`
  grid-area: author;
  align-self: flex-end;
`

const ReadTime = styled.span`
  grid-area: read;
`

const Label = styled.span`
  grid-area: label;
  align-self: center;
  justify-self: flex-end;
  font-size: ${p => p.theme.fontSizes[1]};
  font-weight: ${p => p.theme.fontWeights.bold};
`

const Link = styled(BaseLink)`
  display: grid;
  grid-template-areas: 'title title' 'author label' 'read label';
  grid-template-rows: 6.875rem repeat(2, auto);
  grid-template-columns: auto 3.875rem;
  grid-column-gap: ${p => p.theme.space[1]};
  padding: ${p => p.theme.space[5]};
  font-size: ${p => p.theme.fontSizes[1]};
  border: ${p => `2px solid ${p.theme.colors.black[0]}`};
  box-shadow: ${p => `4px 4px 0 0 ${p.theme.colors.black[0]}`};
  color: ${p => p.theme.colors.text};

  ${mediaQueries.md} {
    grid-column-gap: ${p => p.theme.space[4]};
    grid-template-rows: 8.125rem repeat(2, auto);
  }

  ${mediaQueries.lg} {
    grid-template-areas: 'title title title' 'avatar author label' 'avatar read label';
    grid-template-columns: 3.5rem auto 2.875rem;
    grid-template-rows: 6.25rem repeat(2, auto);

    :hover {
      ${Title} {
        text-decoration: underline;
      }
    }
  }
`

const Avatar = styled(GatsbyImage)`
  grid-area: avatar;
  display: none;

  ${mediaQueries.lg} {
    display: block;
  }
`

export const PostLink = ({ post, className }) => {
  const {
    frontmatter: { title, author, read, avatar },
    fields: { slug },
  } = post
  const label = useLabel(post)

  return (
    <Link className={className} to={slug}>
      <Avatar image={getImage(avatar)} alt={author} loading="lazy" />
      <Title>{title}</Title>
      <Author>{author}</Author>
      <ReadTime>{read} min read </ReadTime>
      {label && <Label>{label}</Label>}
    </Link>
  )
}
